import { createTheme, darken, alpha } from '@mui/material/styles';

const grey = "#757575";
const lightGreen = "#f7fcf6";
const textDefaultColor = "#333333";

const primaryDefault = "#002A54";
const secondaryDefault = "#ffffff";
const infoDefault = "#EBF4F9";
const hoverDefault = "rgba(0, 0, 0, 0.04)";
const shadowDefault = "0 4px 12px rgba(0, 0, 0, 0.15)";
const darkenAmount = 0.1;
const whiteDefault = "#ffffff";
const tooltipTextDefault = 'rgba(0, 0, 0, 0.87)';
export default class Theme {

  static get(primaryColor, secondaryColor, infoColor, hoverColor, shadowColor, whiteColor) {
    const primary = primaryColor || primaryDefault;
    const secondary = secondaryColor || secondaryDefault;
    const info = infoColor || infoDefault;
    const hover = hoverColor || hoverDefault;
    const shadow = shadowColor || shadowDefault;
    const white = whiteColor || whiteDefault;

    const theme = createTheme({
      palette: {
        primary: {
          main: primary,
          contrastText: white,
        },
        secondary: {
          main: secondary,
          contrastText: white,
        },
        info: {
          main: info,
          contrastText: primary,
          dark: darken(info, darkenAmount),
        },
        text: {
          primary: textDefaultColor,
        },
        white: {
          main: white,
        },
        lightGreen: {
          main: lightGreen,
        }
      },
      custom: {
        shadow: { main: shadow, },
        hover: {
          main: hover,
          contrastText: grey,
        },
        tooltip: { main: tooltipTextDefault, },
      },
      typography: {
        fontFamily: "\"Inter\", sans-serif",
        h1: {
          fontWeight: 700,
          fontSize: '6rem',
        },
        h2: {
          fontWeight: 700,
          fontSize: '3.75rem',
        },
        h3: {
          fontWeight: 700,
          fontSize: '3rem',
        },
        h4: {
          fontWeight: 700,
          fontSize: '2.25rem',
        },
        h5: {
          fontWeight: 700,
          fontSize: '1.5rem',
        },
        h6: {
          fontWeight: 700,
          fontSize: '0.875rem',
        },
      },
      breakpoints: {
        values: {
          xs: 0,
          sm: 576,
          md: 768,
          lg: 992,
          xl: 1200,
        },
      },
      components: {
        MuiButton: {
          styleOverrides: {
            containedPrimary: {
              '&:hover': {
                color: white,
              },
              '&:focus': {
                boxShadow: `0 0 5px 0px ${primary}`,
                outline: 'none'
              }
            },
            containedSecondary: {
              '&:focus': {
                outline: 'none'
              }
            },
            containedInfo: {
              backgroundColor: info,
              color: '#333333',
              '&:hover': {
                backgroundColor: alpha(info, .85)
              },
              '&:focus': {
                boxShadow: `0 0 5px 0px ${info}`,
                outline: 'none'
              }
            },
            outlined: {
              padding: '15px',
              boxShadow: '0px 2px 4px 0px lightgrey',
              textTransform: 'none',
            },
            outlinedPrimary: {
              '&:hover': {
                color: primary,
                backgroundColor: alpha(primary, .05),
                border: `1px solid ${alpha(primary, .2)}`,
                outline: 'none',
              },
              '&:focus': {
                backgroundColor: alpha(primary, .15),
                border: `1px solid ${primary}`,
                outline: 'none'
              }
            },
            text: {
              '&:focus': {
                outline: 'none',
                boxShadow: 'none',
              },
              textTransform: 'none',
            },
            textPrimary: {
              fontWeight: 'bold',
              letterSpacing: '1px',
              '&:hover': {
                backgroundColor: alpha(primary, .05),
                color: primary,
              },
              '&:focus': {
                backgroundColor: alpha(primary, .15),
                outline: 'none'
              }
            },
          }
        },
        MuiTextField: {
          styleOverrides: {
            root: {
              backgroundColor: white,
              borderRadius: '4px',
            }
          }
        },
        MuiFilledInput: {
          styleOverrides: {
            root: {
              backgroundColor: white,
              '&:hover': {
                backgroundColor: white,
              },
              '&$focused': {
                backgroundColor: white,
              },
              borderRadius: '4px',
              border: '1px solid #fefefe',
            },
            underline: {
              '&:before': {
                borderBottom: `1px solid ${primary}`,
              },
              '&:hover:not($disabled):not($focused):not($error):before': {
                borderBottom: `2px solid ${primary}`,
              },
              '&:after': {
                borderBottom: `2px solid ${primary}`,
              },
            },
          }
        },
        MuiInputBase: {
          styleOverrides: {
            input: {
              backgroundColor: white,
            },
          }
        },
        MuiOutlinedInput: {
          styleOverrides: {
            notchedOutline: {
              borderColor: grey,
            }
          }
        },
        MuiLinearProgress: {
          styleOverrides: {
            root: {
              height: '.6rem',
              borderRadius: '10px',
            },
            bar: {
              borderRadius: '10px',
            }
          }
        },
        MuiCardContent: {
          styleOverrides: {
            root: {
              padding: '15px',
            }
          }
        },
        MuiTouchRipple: {
          styleOverrides: {
            child: {
              backgroundColor: primary
            }
          }
        },
        MuiTableHead: {
          styleOverrides: {
            root: {
              backgroundColor: info,
            },
          }
        },
        MuiTableRow: {
          styleOverrides: {
            root: {
              '&:hover': {
                backgroundColor: alpha(info, 0.85),
              },
            },
          }
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              borderBottom: `1px solid ${grey}`,
              fontSize: '1rem',
            },
          }
        },
        MuiRadio: {
          styleOverrides: {
            root: {
              color: primary,
              '&$checked': {
                color: primary,
              },
            },
            colorSecondary: {
              '&$checked': {
                color: primary,
              },
            },
          }
        },
      }
    });
    return theme;
  }
}
